import { Download } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Grid, Stack } from "@mui/material";
import { FormDatePicker, FormTextField } from "components/atoms";
import { DetailTableTabs, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { TimeHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import {
  IDownloadExcelRequest,
  IPackingLogByQR,
  IPaginateApiResponse,
  IPaginationRequest,
  IQuantityMoving,
} from "model";
import moment from "moment";
import { useState } from "react";
import { DownloadActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { ReportApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import { PackingLog, PackingLogDetail } from "./components";

const TransactionOutputReport = () => {
  const dispatch = useAppDispatch();

  const [request, setRequest] = useState<IPaginationRequest>({
    fromDate: TimeHelper.startOfWeek("MM-DD-YYYY"),
    toDate: TimeHelper.endOfWeek("MM-DD-YYYY"),
    orderBy: "PackagingDate",
  });

  const [packingLogRequest, setPackingLogRequest] =
    useState<IPaginationRequest>({
      ...request,
      pageNumber: FIRST_PAGE,
      pageSize: CURRENT_PAGE_SIZE,
    });
  const [packingLogByQRRequest, setPackingLogByQRRequest] =
    useState<IPaginationRequest>({
      ...request,
      pageNumber: FIRST_PAGE,
      pageSize: CURRENT_PAGE_SIZE,
    });

  const handelSubmit = (request: IPaginationRequest) => {
    setRequest((prev) => ({
      ...prev,
      ...request,
      fromDate: request.fromDate,
      toDate: request.toDate,
    }));
    setPackingLogRequest({ ...packingLogRequest, ...request });
    setPackingLogByQRRequest({ ...packingLogRequest, ...request });
  };
  const handlePackingLogChangePage = (pageNumber: number) => {
    setPackingLogRequest({
      ...packingLogRequest,
      pageNumber: pageNumber,
    });
  };
  const handlePackingLogPageSizeChange = (pageSize: number) => {
    setPackingLogRequest({ ...packingLogRequest, pageSize: pageSize });
  };
  const handlePackingLogSearchTextChange = (searchText: string) => {
    setRequest((prev) => ({
      ...prev,
      ...request,
      pageNumber: FIRST_PAGE,
      searchText: searchText,
    }));
    setPackingLogRequest({
      ...packingLogRequest,
      pageNumber: FIRST_PAGE,
      searchText: searchText,
    });
  };

  const handlePackingLogByQRChangePage = (pageNumber: number) => {
    setPackingLogByQRRequest({
      ...packingLogByQRRequest,
      pageNumber: pageNumber,
    });
  };
  const handlePackingLogByQRPageSizeChange = (pageSize: number) => {
    setPackingLogByQRRequest({ ...packingLogByQRRequest, pageSize: pageSize });
  };
  const handlePackingLogByQRSearchTextChange = (searchText: string) => {
    setRequest((prev) => ({
      ...prev,
      ...request,
      pageNumber: FIRST_PAGE,
      searchText: searchText,
    }));
    setPackingLogByQRRequest({
      ...packingLogByQRRequest,
      pageNumber: FIRST_PAGE,
      searchText: searchText,
    });
  };

  const { loading: packingLogLoading, result: packingLogResult } =
    usePaginationRequest<IPaginationRequest, IQuantityMoving[]>(
      packingLogRequest,
      ReportApiService.getFinishedProductPalletWaitToWarehouse,
      [packingLogRequest]
    );
  const { loading: packingLogByQRLoading, result: packingLogByQRResult } =
    usePaginationRequest<IPaginationRequest, IPackingLogByQR[]>(
      packingLogByQRRequest,
      ReportApiService.getPackingLogByQRCode,
      [packingLogByQRRequest]
    );

  const downloadPackingReport = () => {
    const params: IDownloadExcelRequest = {
      ...request,
      pageSize: (packingLogResult as IPaginateApiResponse<IQuantityMoving[]>)
        .totalRecords,
      pageNumber: FIRST_PAGE,
      fromDate: TimeHelper.formatDate(
        request?.fromDate! as string,
        "YYYY-MM-DD"
      ),
      toDate: TimeHelper.formatDate(request?.toDate! as string, "YYYY-MM-DD"),
      fileName: EXPORT_FILE_NAME.packingReport,
    };
    dispatch(DownloadActions.downloadPackingReport.request(params));
  };

  const tabsName = [
    I18nTranslator(translations.report.packingDetail),
    I18nTranslator(translations.report.packingDetailByQRCode),
  ];

  const tabsPanel = [
    <PackingLog
      loading={packingLogLoading}
      result={packingLogResult}
      request={request}
      onChangePage={handlePackingLogChangePage}
      onPageSizeChange={handlePackingLogPageSizeChange}
      onSearch={handlePackingLogSearchTextChange}
    />,
    <PackingLogDetail
      loading={packingLogByQRLoading}
      result={packingLogByQRResult}
      request={request}
      onChangePage={handlePackingLogByQRChangePage}
      onPageSizeChange={handlePackingLogByQRPageSizeChange}
      onSearch={handlePackingLogByQRSearchTextChange}
    />,
  ];
  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
      }}
    >
      <DetailHeader
        request={request}
        onSubmit={handelSubmit}
        downloadPackingReport={downloadPackingReport}
      />
      <DetailTableTabs
        hideHeaderButton
        tabsName={tabsName}
        tabsPanel={tabsPanel}
      />
    </div>
  );
};

const DetailHeader = (props: {
  request: IPaginationRequest;
  onSubmit(values: IPaginationRequest): void;
  downloadPackingReport(): void;
}) => {
  const { request, onSubmit, downloadPackingReport } = props;
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );

  const formik = useFormik<any>({
    initialValues: {
      fromDate: moment(request.fromDate, "MM-DD-YYYY"),
      toDate: moment(request.toDate, "MM-DD-YYYY"),
      searchText: "",
    },
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      onSubmit({
        searchText: values.searchText,
        pageNumber: 1,
        pageSize: CURRENT_PAGE_SIZE,
        fromDate: TimeHelper.formatMomentToDate(values.fromDate, "MM-DD-YYYY"),
        toDate: TimeHelper.formatMomentToDate(values.toDate, "MM-DD-YYYY"),
      });
    },
  });

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.packagingLog)}
      formik={formik}
      loading={false}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <LoadingButton
            startIcon={<Download />}
            loading={downloading}
            variant="contained"
            onClick={downloadPackingReport}
          >
            {I18nTranslator(translations.button.exportDocs)}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={3} item>
          <FormDatePicker
            id="fromDate"
            label={I18nTranslator(translations.fromDate)}
            defaultValue={TimeHelper.formatDate(formik.values.deliveryDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormDatePicker
            id="toDate"
            label={I18nTranslator(translations.toDate)}
            defaultValue={TimeHelper.formatDate(formik.values.deliveryDate)}
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.search)}
            placeholder={"Tìm kiếm: Mã lô, Mã QR mã hóa"}
            id="searchText"
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

export default TransactionOutputReport;
